'use client';

import { c as _c } from "react/compiler-runtime";
import { keepPreviousData } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { hasValidCoordinates } from '@sola/skippy-search-sdk/lib/geo.ts';
import { useListVolunteerLocationsQuery } from '@sola/skippy-search-sdk/lib/queries.ts';
import type { VolunteerLocationItemDto } from '@sola/skippy-search-sdk/lib/schema.ts';
import type { TrackVolunteerListProps } from '@sola/tracking/components/datalayer-volunteer.tsx';
import { useVolunteerQueryParams } from '../../utils/query-params.tsx';

// ─── Column Layout ───────────────────────────────────────────────────────────

/** Derive column counts from limit. */
export const buildColumnLayout = (limit: number) => ({
  columns: 1,
  columnsMd: Math.min(limit, 3),
  columnsLg: Math.min(limit, 3)
});

// ─── Hook ────────────────────────────────────────────────────────────────────

export type UseVolunteerListOptions = {
  q?: string;
  limit?: number;
  variant?: 'default' | 'tour';
  slug?: string;
  isDynamic?: boolean;
  onVolunteerLoad?: (info: {
    totalCount: number;
  }) => void;
  geoCoords?: {
    city?: string;
    state?: string;
    latitude?: number;
    longitude?: number;
  };
  /** True while the passive location/geo is still being resolved. */
  isLocationResolving?: boolean;
};
export const useVolunteerList = t0 => {
  const $ = _c(57);
  const {
    limit,
    variant: t1,
    slug,
    isDynamic: t2,
    onVolunteerLoad,
    geoCoords,
    isLocationResolving: t3
  } = t0;
  const variant = t1 === undefined ? "default" : t1;
  const isDynamic = t2 === undefined ? false : t2;
  const isLocationResolving = t3 === undefined ? false : t3;
  let t4;
  if ($[0] !== limit) {
    t4 = {
      perPage: limit
    };
    $[0] = limit;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const {
    volunteerFilters
  } = useVolunteerQueryParams(t4);
  const [currentPage, setCurrentPage] = useState(1);
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [];
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const [allResults, setAllResults] = useState(t5);
  const [pendingNewQuery, setPendingNewQuery] = useState(false);
  const hasMountedRef = useRef(false);
  const isTour = variant === "tour";
  const sortBy = isDynamic ? volunteerFilters.sortby || "relevance" : "relevance";
  const resolvedLimit = isDynamic ? volunteerFilters.limit || limit || 12 : limit || 12;
  let t6;
  if ($[3] !== resolvedLimit) {
    t6 = buildColumnLayout(resolvedLimit);
    $[3] = resolvedLimit;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const columnLayout = t6;
  const fetchSize = resolvedLimit + 1;
  const hasGeo = hasValidCoordinates(geoCoords?.latitude, geoCoords?.longitude);
  let apiSortBy = "relevance";
  if (sortBy === "location" && hasGeo) {
    apiSortBy = "distance";
  } else {
    if (sortBy === "date") {
      apiSortBy = "date";
    }
  }
  const t7 = geoCoords?.latitude;
  const t8 = geoCoords?.longitude;
  const t9 = volunteerFilters.q || undefined;
  const t10 = (currentPage - 1) * resolvedLimit;
  const t11 = isTour && slug ? slug : undefined;
  let t12;
  if ($[5] !== apiSortBy || $[6] !== fetchSize || $[7] !== t10 || $[8] !== t11 || $[9] !== t7 || $[10] !== t8 || $[11] !== t9) {
    t12 = {
      latitude: t7,
      longitude: t8,
      sort: apiSortBy,
      query: t9,
      perPage: fetchSize,
      offset: t10,
      slug: t11
    };
    $[5] = apiSortBy;
    $[6] = fetchSize;
    $[7] = t10;
    $[8] = t11;
    $[9] = t7;
    $[10] = t8;
    $[11] = t9;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  const t13 = pendingNewQuery ? undefined : keepPreviousData;
  let t14;
  if ($[13] !== t13) {
    t14 = {
      placeholderData: t13
    };
    $[13] = t13;
    $[14] = t14;
  } else {
    t14 = $[14];
  }
  const volunteerQuery = useListVolunteerLocationsQuery(t12, t14);
  const {
    data: volunteerLocationsData,
    isLoading,
    isFetching,
    isError
  } = volunteerQuery;
  const totalCount = volunteerLocationsData?.totalCount || 0;
  const showLoading = isLoading || pendingNewQuery && isFetching;
  const geoLat = geoCoords?.latitude;
  const geoLng = geoCoords?.longitude;
  let t15;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => {
      if (!hasMountedRef.current) {
        hasMountedRef.current = true;
        return;
      }
      setCurrentPage(1);
      setPendingNewQuery(true);
    };
    $[15] = t15;
  } else {
    t15 = $[15];
  }
  let t16;
  if ($[16] !== geoLat || $[17] !== geoLng || $[18] !== volunteerFilters.q || $[19] !== volunteerFilters.sortby) {
    t16 = [volunteerFilters.sortby, volunteerFilters.q, geoLat, geoLng];
    $[16] = geoLat;
    $[17] = geoLng;
    $[18] = volunteerFilters.q;
    $[19] = volunteerFilters.sortby;
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  useEffect(t15, t16);
  let t17;
  let t18;
  if ($[21] !== currentPage || $[22] !== resolvedLimit || $[23] !== volunteerLocationsData) {
    t17 = () => {
      if (!volunteerLocationsData?.items) {
        return;
      }
      const newResults = volunteerLocationsData.items.slice(0, resolvedLimit);
      if (currentPage === 1) {
        setAllResults(newResults);
      } else {
        setAllResults(prev => [...prev, ...newResults]);
      }
      setPendingNewQuery(false);
    };
    t18 = [volunteerLocationsData, currentPage, resolvedLimit];
    $[21] = currentPage;
    $[22] = resolvedLimit;
    $[23] = volunteerLocationsData;
    $[24] = t17;
    $[25] = t18;
  } else {
    t17 = $[24];
    t18 = $[25];
  }
  useEffect(t17, t18);
  const ready = isTour || !isLocationResolving && !pendingNewQuery;
  const t19 = volunteerLocationsData?.items;
  const t20 = volunteerFilters.q || "";
  const t21 = isTour ? slug : undefined;
  const t22 = isTour ? volunteerLocationsData?.items?.[0]?.tourName : undefined;
  let t23;
  if ($[26] !== currentPage || $[27] !== isFetching || $[28] !== ready || $[29] !== resolvedLimit || $[30] !== sortBy || $[31] !== t19 || $[32] !== t20 || $[33] !== t21 || $[34] !== t22) {
    t23 = {
      page: currentPage,
      items: t19,
      limit: resolvedLimit,
      sortBy,
      query: t20,
      isFetching,
      ready,
      slug: t21,
      tourName: t22
    };
    $[26] = currentPage;
    $[27] = isFetching;
    $[28] = ready;
    $[29] = resolvedLimit;
    $[30] = sortBy;
    $[31] = t19;
    $[32] = t20;
    $[33] = t21;
    $[34] = t22;
    $[35] = t23;
  } else {
    t23 = $[35];
  }
  const tracking = t23;
  let t24;
  let t25;
  if ($[36] !== isLoading || $[37] !== onVolunteerLoad || $[38] !== totalCount) {
    t24 = () => {
      if (onVolunteerLoad && !isLoading) {
        onVolunteerLoad({
          totalCount
        });
      }
    };
    t25 = [totalCount, isLoading, onVolunteerLoad];
    $[36] = isLoading;
    $[37] = onVolunteerLoad;
    $[38] = totalCount;
    $[39] = t24;
    $[40] = t25;
  } else {
    t24 = $[39];
    t25 = $[40];
  }
  useEffect(t24, t25);
  let t26;
  if ($[41] !== allResults) {
    t26 = allResults.filter(_temp);
    $[41] = allResults;
    $[42] = t26;
  } else {
    t26 = $[42];
  }
  const uniqueLocations = t26;
  let t27;
  if ($[43] !== volunteerLocationsData?.items) {
    t27 = volunteerLocationsData?.items ?? [];
    $[43] = volunteerLocationsData?.items;
    $[44] = t27;
  } else {
    t27 = $[44];
  }
  const lastPageItems = t27;
  const hasMore = lastPageItems.length > resolvedLimit;
  let t28;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = () => {
      setCurrentPage(_temp2);
    };
    $[45] = t28;
  } else {
    t28 = $[45];
  }
  const handleLoadMore = t28;
  const t29 = isFetching && currentPage > 1;
  let t30;
  if ($[46] !== columnLayout || $[47] !== hasMore || $[48] !== isError || $[49] !== resolvedLimit || $[50] !== showLoading || $[51] !== t29 || $[52] !== totalCount || $[53] !== tracking || $[54] !== uniqueLocations || $[55] !== volunteerLocationsData) {
    t30 = {
      locations: uniqueLocations,
      volunteerLocationsData,
      isLoading: showLoading,
      isLoadingMore: t29,
      isError,
      totalCount,
      resolvedLimit,
      columnLayout,
      hasMore,
      handleLoadMore,
      tracking
    };
    $[46] = columnLayout;
    $[47] = hasMore;
    $[48] = isError;
    $[49] = resolvedLimit;
    $[50] = showLoading;
    $[51] = t29;
    $[52] = totalCount;
    $[53] = tracking;
    $[54] = uniqueLocations;
    $[55] = volunteerLocationsData;
    $[56] = t30;
  } else {
    t30 = $[56];
  }
  return t30;
};
function _temp(a, i, arr) {
  return arr.findIndex(b => b.eventId === a.eventId) === i;
}
function _temp2(prev_0) {
  return prev_0 + 1;
}